import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Folder, Loader2, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { useMemberSections } from "@/components/dashboard/memberNav";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

/**
 * Lists every data room the current authenticated user has been granted
 * access to. Admins see all rooms.
 */
export default function MyDataRooms() {
  const { t } = useTranslation();
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, rooms: [] });
  const sections = useMemberSections({ role: user?.role, unreadCount: unreadCount || 0 });
  const goToTab = useCallback((id) => navigate(`/dashboard?tab=${id}`), [navigate]);

  useEffect(() => {
    if (authLoading || !user) return;
    let alive = true;
    (async () => {
      try {
        const res = await client.get("/data-rooms/mine");
        if (!alive) return;
        setState({ loading: false, rooms: res.data?.items || [] });
      } catch (_) {
        if (alive) setState({ loading: false, rooms: [] });
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="my-data-rooms-auth">
        <LockKeyhole className="h-8 w-8 text-primary mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold">{t("dataRoom.memberLoginRequiredTitle")}</h1>
        <p className="text-sm text-muted-foreground mt-2">{t("dataRoom.myLoginBody")}</p>
        <Button className="btn-brand rounded-xl mt-6" onClick={() => loginWithGoogle()}>
          {t("dataRoom.memberLoginCta")}
        </Button>
      </div>
    );
  }

  return (
    <div className="overflow-x-clip" data-testid="my-data-rooms-page">
      <DashboardShell
        sections={sections}
        active="rooms"
        onSelect={goToTab}
        title={t("dataRoom.myTitle")}
        subtitle={t("dataRoom.mySubtitle")}
      >
      {state.loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : state.rooms.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="my-data-rooms-empty">
          <Folder className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t("dataRoom.myEmpty")}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {state.rooms.map((r) => (
            <Link key={r.room_id} to={`/data-rooms/${r.room_id}`} data-testid={`my-data-rooms-card-${r.room_id}`}>
              <Card className="rounded-2xl border border-border p-5 hover:border-primary/40 hover:shadow-md transition-all h-full">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0">
                    <Folder className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-semibold truncate">{r.company_name}</p>
                    {r.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{r.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <span>{r.document_count} {t("dataRoom.docsShort")}</span>
                      <span>·</span>
                      <span>{fmtDate(r.updated_at)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
      </DashboardShell>
    </div>
  );
}
